import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Button, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { ContactStatus, IContact } from "../../../interfaces";
import { formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { removeAcento } from "../../../../../utils/RemoveAcentos";
import { ThemeContext } from "../../../../../contexts/ThemeContext";
import useAuth from "../../../../../hooks/useAuth";
import HelpAndSupportService from "../../../../../services/http/services/HelpAndSupportService";
import { EmitError } from "../../../../../utils/EmitError";
import TableActionButton from "../../../../../components/TableActionButton";
import { IActiveFilterItem } from "../../../../../components/ActiveFiltersBar";
import useTableColumnFilter from "../../../../../hooks/useTableColumnFilter";

export interface ITableData extends IContact {
    key: number;
    app_user_name: string;
}

export type ViewedFilter = "PANEL_UNREAD" | "PANEL_READ" | "APP_UNREAD" | "APP_READ" | null;

export interface ITabAppUsersFilters {
    search: string | null;
    status: ContactStatus | null;
    viewed: ViewedFilter;
}

export type SortField = "title" | "message" | "app_user_name" | "status" | "panel_user_viewed" | "created_at" | null;

export default function useTabAppUsers() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<ITabAppUsersFilters>({
        search: null,
        status: null,
        viewed: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: "asc" | "desc" | null }>({
        field: "created_at",
        order: "desc",
    });

    const [searchTitleQuery, setSearchTitleQuery] = useState("");
    const [searchUserQuery, setSearchUserQuery] = useState("");

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const handleClickViewHelpAndSupport = useCallback(
        (data: IContact) => {
            navigate(`/ajuda-e-suporte/usuarios/${data.id}`);
        },
        [navigate]
    );

    const handleFilterChange = useCallback((key: keyof ITabAppUsersFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            search: null,
            status: null,
            viewed: null,
        });
        setSearchTitleQuery("");
        setSearchUserQuery("");
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const stats = useMemo(() => {
        const total = rawData.length;
        const open = rawData.filter((r) => r.status === ContactStatus.OPEN).length;
        const closed = rawData.filter((r) => r.status === ContactStatus.CLOSED).length;
        const notViewed = rawData.filter((r) => !r.panel_user_viewed).length;

        return { total, open, closed, notViewed };
    }, [rawData]);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.search) {
            const query = removeAcento(filters.search.trim().toLowerCase());
            result = result.filter((item) => {
                const title = item.title ? removeAcento(item.title.toLowerCase()) : "";
                const message = item.message ? removeAcento(item.message.toLowerCase()) : "";
                const userName = item.app_user_name ? removeAcento(item.app_user_name.toLowerCase()) : "";
                const subject = item.subject_contact?.name ? removeAcento(item.subject_contact.name.toLowerCase()) : "";
                const attendant = item.panel_user?.name ? removeAcento(item.panel_user.name.toLowerCase()) : "";

                return (
                    title.includes(query) ||
                    message.includes(query) ||
                    userName.includes(query) ||
                    subject.includes(query) ||
                    attendant.includes(query)
                );
            });
        }

        if (filters.status) {
            result = result.filter((item) => item.status === filters.status);
        }

        if (filters.viewed) {
            if (filters.viewed === "PANEL_UNREAD") {
                result = result.filter((item) => !item.panel_user_viewed);
            } else if (filters.viewed === "PANEL_READ") {
                result = result.filter((item) => item.panel_user_viewed === true);
            } else if (filters.viewed === "APP_UNREAD") {
                result = result.filter((item) => item.app_user_viewed === false);
            } else if (filters.viewed === "APP_READ") {
                result = result.filter((item) => item.app_user_viewed === true && Boolean(item.app_user_viewed_at));
            }
        }

        return result;
    }, [rawData, filters]);

    const sortedData = useMemo(() => {
        if (!sorter.field || !sorter.order) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA: any = a[sorter.field as keyof ITableData];
            let valB: any = b[sorter.field as keyof ITableData];

            if (sorter.field === "created_at") {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            } else if (typeof valA === "string") {
                valA = removeAcento(valA.toLowerCase());
                valB = removeAcento((valB || "").toLowerCase());
            }

            if (valA < valB) return sorter.order === "asc" ? -1 : 1;
            if (valA > valB) return sorter.order === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sorter]);

    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return sortedData.slice(start, start + pagination.pageSize);
    }, [sortedData, pagination]);

    const activeFilterItems: IActiveFilterItem[] = useMemo(() => {
        const items: IActiveFilterItem[] = [];

        if (filters.search) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${filters.search}"`,
                onRemove: () => {
                    handleFilterChange("search", null);
                    setSearchTitleQuery("");
                    setSearchUserQuery("");
                },
            });
        }

        if (filters.status) {
            items.push({
                key: "status",
                label: "Status",
                value: filters.status === ContactStatus.OPEN ? "Aberto" : "Fechado",
                onRemove: () => handleFilterChange("status", null),
            });
        }

        if (filters.viewed) {
            const viewedLabels: Record<string, string> = {
                PANEL_UNREAD: "Não lidas (Suporte)",
                PANEL_READ: "Lidas (Suporte)",
                APP_UNREAD: "Não visualizadas (Usuário)",
                APP_READ: "Visualizadas (Usuário)",
            };
            items.push({
                key: "viewed",
                label: "Visualização",
                value: viewedLabels[filters.viewed] || filters.viewed,
                onRemove: () => handleFilterChange("viewed", null),
            });
        }

        return items;
    }, [filters, handleFilterChange]);

    const handleTableChange = useCallback((newPagination: any, tableFilters: any, newSorter: any) => {
        if (Array.isArray(newSorter)) {
            newSorter = newSorter[0];
        }

        if (newSorter && newSorter.field) {
            const order = newSorter.order === "ascend" ? "asc" : newSorter.order === "descend" ? "desc" : null;
            setSorter({
                field: order ? (newSorter.field as SortField) : null,
                order,
            });
        } else {
            setSorter({ field: null, order: null });
        }
    }, []);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns: ColumnsType<ITableData> = useMemo(() => {
        const getSortOrder = (field: SortField): SortOrder | undefined => {
            if (sorter.field !== field) return undefined;
            return sorter.order === "asc" ? "ascend" : sorter.order === "desc" ? "descend" : undefined;
        };

        return [
            {
                title: "Assunto / Título",
                dataIndex: "title",
                key: "title",
                sorter: true,
                sortOrder: getSortOrder("title"),
                ...getColumnSearchProps({
                    title: "Filtrar por Título",
                    placeholder: "Título do chamado...",
                    value: searchTitleQuery,
                    onChange: setSearchTitleQuery,
                    onSearch: (val) => handleFilterChange("search", val),
                    onClear: () => handleFilterChange("search", null),
                    isActive: Boolean(filters.search),
                }),
                render: (_: any, record: ITableData) => (
                    <div>
                        <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 13.5 }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={filters.search ? [filters.search] : []}
                                autoEscape
                                textToHighlight={record.title || "--"}
                            />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                            {record.subject_contact?.name && (
                                <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                                    {record.subject_contact.name}
                                </Tag>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                title: "Usuário",
                dataIndex: "app_user_name",
                key: "app_user_name",
                width: 240,
                sorter: true,
                sortOrder: getSortOrder("app_user_name"),
                ...getColumnSearchProps({
                    title: "Filtrar por Usuário",
                    placeholder: "Nome do usuário...",
                    value: searchUserQuery,
                    onChange: setSearchUserQuery,
                    onSearch: (val) => handleFilterChange("search", val),
                    onClear: () => handleFilterChange("search", null),
                    isActive: Boolean(filters.search),
                }),
                render: (_: any, record: ITableData) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                        {record.app_user?.id && (
                            <Tooltip title="Ver detalhes do usuário">
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<UserOutlined style={{ fontSize: 11 }} />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/usuarios/aplicativo/visualizar/${record.app_user?.id}`);
                                    }}
                                    style={{
                                        backgroundColor: "#1677ff",
                                        width: 22,
                                        height: 22,
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 4,
                                        flexShrink: 0,
                                    }}
                                />
                            </Tooltip>
                        )}
                        <span style={{ fontWeight: 500, fontSize: 12.5, color: isDark ? "#d9d9d9" : "#334155", whiteSpace: "nowrap" }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={filters.search ? [filters.search] : []}
                                autoEscape
                                textToHighlight={record.app_user_name || "--"}
                            />
                        </span>
                    </div>
                ),
            },
            {
                title: "Atendente",
                dataIndex: "panel_user",
                key: "panel_user",
                width: 150,
                render: (_: any, record: ITableData) =>
                    record.panel_user?.name ? (
                        <span style={{ fontWeight: 500, fontSize: 12.5, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                            {record.panel_user.name}
                        </span>
                    ) : (
                        <span style={{ color: isDark ? "#666" : "#94a3b8", fontSize: 12 }}>--</span>
                    ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: 120,
                sorter: true,
                sortOrder: getSortOrder("status"),
                ...getColumnRadioProps({
                    title: "Filtrar por Status",
                    value: filters.status || "",
                    onChange: (val) => handleFilterChange("status", (val as ContactStatus) || null),
                    onClear: () => handleFilterChange("status", null),
                    isActive: Boolean(filters.status),
                    options: [
                        { label: "Todos os Status", value: "" },
                        { label: "Aberto", value: ContactStatus.OPEN },
                        { label: "Fechado", value: ContactStatus.CLOSED },
                    ],
                }),
                render: (val: ContactStatus) =>
                    val === ContactStatus.OPEN ? (
                        <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                            Aberto
                        </Tag>
                    ) : (
                        <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                            Fechado
                        </Tag>
                    ),
            },
            {
                title: "Data do Cadastro",
                dataIndex: "created_at",
                key: "created_at",
                width: 160,
                sorter: true,
                sortOrder: getSortOrder("created_at"),
                render: (val: string) => (
                    <span style={{ fontSize: 12.5, color: isDark ? "#d9d9d9" : "#475569", whiteSpace: "nowrap" }}>
                        {val ? formatDateStringToDateAndTime(val) : "--"}
                    </span>
                ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 80,
                align: "center" as const,
                render: (_: any, record: ITableData) => (
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar e Responder Chamado"
                        onClick={() => handleClickViewHelpAndSupport(record)}
                    />
                ),
            },
        ];
    }, [
        isDark,
        sorter,
        filters.search,
        filters.status,
        filters.viewed,
        searchTitleQuery,
        searchUserQuery,
        getColumnSearchProps,
        getColumnRadioProps,
        handleFilterChange,
        handleClickViewHelpAndSupport,
        navigate,
    ]);

    const setTableDataFromRequest = useCallback((data: Array<IContact>) => {
        const newData: ITableData[] = data.map((item) => ({
            ...item,
            key: item.id,
            app_user_name: item.app_user?.name || "Cidadão",
        }));
        setRawData(newData);
    }, []);

    const fetchContacts = useCallback(
        async (controller: AbortSignal, silent = false) => {
            if (!activeCity?.id) return;
            try {
                if (!silent) {
                    setIsLoading(true);
                }
                const list = await HelpAndSupportService.listContacts(activeCity.id, controller);
                setTableDataFromRequest(list);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }
                EmitError(err, true, "help_and_support_load_error");
            } finally {
                if (!silent) {
                    setIsLoading(false);
                }
            }
        },
        [activeCity?.id, setTableDataFromRequest]
    );

    const reloadData = useCallback(async () => {
        const controller = new AbortController();
        await fetchContacts(controller.signal, false);
    }, [fetchContacts]);

    useEffect(() => {
        if (!activeCity?.id) return;

        const controller = new AbortController();
        fetchContacts(controller.signal, false);

        return () => {
            controller.abort();
        };
    }, [activeCity?.id, fetchContacts]);

    return {
        isLoading,
        columns,
        tableData: paginatedData,
        totalCount: sortedData.length,
        stats,
        pagination: {
            ...pagination,
            total: sortedData.length,
        },
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickViewHelpAndSupport,
        reloadData,
    };
}
